
import { useMemo } from "react";
import { 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  format, 
  isToday,
  parseISO
} from "date-fns";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { CalendarContentItem } from "@/hooks/useContentCalendar";
import { FileText, Linkedin } from "lucide-react";

interface CalendarMonthViewProps {
  currentDate: Date;
  items: CalendarContentItem[];
  onItemClick: (item: CalendarContentItem) => void;
}

export const CalendarMonthView = ({ currentDate, items, onItemClick }: CalendarMonthViewProps) => {
  const calendarDays = useMemo(() => {
    // Get the start/end dates for the current month view
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    
    // Get all days in the calendar view
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);
  
  // Group items by day
  const itemsByDay = useMemo(() => {
    const grouped: Record<string, CalendarContentItem[]> = {};
    
    items.forEach(item => {
      let dateKey: string | null = null;
      
      // Try to use scheduledFor date first
      if (item.scheduledFor) {
        dateKey = format(new Date(item.scheduledFor), "yyyy-MM-dd");
      } 
      // Fall back to publishedAt if scheduledFor is not available
      else if (item.publishedAt) {
        dateKey = format(new Date(item.publishedAt), "yyyy-MM-dd");
      }
      
      // If we have a valid date, add the item to that day's array
      if (dateKey) {
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        
        // Check if this item is already in this day's array (avoid duplicates)
        const isDuplicate = grouped[dateKey].some(existingItem => 
          existingItem.id === item.id && existingItem.sourceType === item.sourceType);
        
        if (!isDuplicate) {
          grouped[dateKey].push(item);
        }
      }
    });
    
    return grouped;
  }, [items]);
  
  // Get items for a specific day
  const getItemsForDay = (day: Date): CalendarContentItem[] => {
    const dateKey = format(day, "yyyy-MM-dd");
    return itemsByDay[dateKey] || [];
  };
  
  // Render the weekday header
  const renderWeekdayHeader = () => {
    const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    
    return (
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekdays.map((day, index) => (
          <div 
            key={index} 
            className="text-center text-sm font-medium text-slate-500 p-1"
          >
            {day}
          </div>
        ))}
      </div>
    );
  };
  
  // Get icon based on content type
  const getContentTypeIcon = (item: CalendarContentItem) => {
    if (item.sourceType === 'blog') {
      return <FileText className="h-3 w-3 text-blue-600" />;
    } else {
      return <Linkedin className="h-3 w-3 text-blue-700" />;
    }
  };
  
  return (
    <div>
      {renderWeekdayHeader()}
      
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day, index) => {
          const isCurrentMonth = isSameMonth(day, currentDate);
          const itemsForDay = getItemsForDay(day);
          const dayIsToday = isToday(day);
          
          return (
            <div 
              key={index} 
              className={cn(
                "h-28 p-1 border rounded-sm overflow-hidden transition-colors",
                !isCurrentMonth && "bg-slate-50 text-slate-300 border-slate-100",
                isCurrentMonth && "border-slate-200 hover:bg-slate-50",
                dayIsToday && "bg-blue-50 border-blue-200",
                isCurrentMonth && "cursor-pointer"
              )}
            >
              <div className="flex justify-between items-start">
                <span className={cn(
                  "text-sm font-semibold",
                  dayIsToday && "text-blue-600"
                )}>
                  {format(day, "d")}
                </span>
                
                {itemsForDay.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {itemsForDay.length}
                  </Badge>
                )}
              </div>
              
              <div className="mt-1 space-y-1 overflow-hidden max-h-[calc(100%-20px)]">
                {itemsForDay.slice(0, 3).map((item) => {
                  // Determine which date to display (publishedAt for published items, scheduledFor for others)
                  const displayDate = item.status === 'published' && item.publishedAt 
                    ? item.publishedAt 
                    : item.scheduledFor;
                  
                  return (
                    <div 
                      key={`${item.sourceType}-${item.id}`} 
                      className={cn(
                        "text-xs p-1 rounded truncate cursor-pointer flex items-center",
                        item.status === 'published' ? "bg-green-100 hover:bg-green-200" :
                        item.status === 'scheduled' ? "bg-blue-100 hover:bg-blue-200" :
                        "bg-amber-100 hover:bg-amber-200"
                      )}
                      onClick={() => onItemClick(item)}
                      title={item.title}
                    >
                      <span className="mr-1 flex-shrink-0">
                        {getContentTypeIcon(item)}
                      </span>
                      {displayDate && (
                        <span className="mr-1">
                          {format(new Date(displayDate), "HH:mm")}
                        </span>
                      )}
                      <span className="truncate">{item.title}</span>
                    </div>
                  );
                })}
                
                {itemsForDay.length > 3 && (
                  <div className="text-xs text-slate-500 pl-1">
                    + {itemsForDay.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
